import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'motion/react';
import { Video, Calendar, Clock, Users, MessageCircle, Bell } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import './LiveQA.css';

function LiveQA({ user }) {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [submittedQuestion, setSubmittedQuestion] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch questions for the upcoming event when it's available
  useEffect(() => {
    const event = upcomingEvents.find(e => e.status === 'upcoming') || upcomingEvents[0];
    if (event) {
      fetchQuestions(event.id);
    }
  }, [upcomingEvents]);

      const fetchEvents = async () => {
        try {
          // In production, this would fetch from Firestore
          // For now, using mock data
      
      const mockUpcoming = [
        {
          id: '1',
          title: 'The Art of French Pastry',
          chef: 'Chef Amélie Dubois',
          chefImage: 'https://images.unsplash.com/photo-1647483684830-7ddde27dcf4d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwb3duZXIlMjBzbWlsaW5nfGVufDF8fHx8MTc2NDA5ODkwNXww&ixlib=rb-4.1.0&q=80&w=1080',
          chefBio: 'Pastry Chef at Le Cordon Bleu, Paris',
          date: 'Friday, December 12, 2025',
          time: '3:00 PM EST',
          duration: 60,
          registered: 342,
          status: 'upcoming',
          topics: ['Croissants & Lamination', 'Macarons', 'Tarts & Pastry Cream', 'Sourdough Starter'],
        }
      ];

      const mockLive = [
        {
          id: '2',
          title: 'Modern Asian Fusion Techniques',
          chef: 'Chef Yuki Tanaka',
          chefImage: 'https://images.unsplash.com/photo-1759521296047-89338c8e083d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWx8ZW58MXx8fHwxNzYzOTg4NTQwfDA&ixlib=rb-4.1.0&q=80&w=1080',
          chefBio: 'Executive Chef at Sakura NYC',
          date: 'Tuesday, December 16, 2025',
          time: '7:00 PM EST',
          duration: 60,
          registered: 456,
          status: 'live',
          topics: ['Japanese Knife Skills', 'Umami Flavors', 'Fusion Concepts', 'Plating Techniques'],
        }
      ];

      const mockPast = [
        {
          id: '3',
          title: 'Farm-to-Table Philosophy',
          chef: 'Chef Sarah Johnson',
          chefImage: 'https://images.unsplash.com/photo-1659354218430-ac7f0b31e977?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwbGl2ZSUyMGNvb2tpbmclMjBkZW1vbnN0cmF0aW9ufGVufDF8fHx8MTc2NDA5OTExM3ww&ixlib=rb-4.1.0&q=80&w=1080',
          chefBio: 'Chef & Owner of Garden Fresh',
          date: 'Monday, November 18, 2025',
          time: '6:00 PM EST',
          duration: 60,
          registered: 289,
          status: 'past',
          topics: ['Seasonal Cooking', 'Local Sourcing', 'Sustainable Practices', 'Menu Planning'],
        }
      ];

      // Combine upcoming and live events
      setUpcomingEvents([...mockUpcoming, ...mockLive]);
      setPastEvents(mockPast);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (eventId) => {
    try {
      const q = query(
        collection(db, 'qaQuestions'),
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const questionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionsData);
    } catch (error) {
      console.error('Error fetching questions:', error);
      // Use mock questions if Firestore query fails
      setQuestions([
        { id: '1', text: 'What\'s your favorite dish to make?', user: 'foodlover123', votes: 5 },
        { id: '2', text: 'How do you source your ingredients?', user: 'chefenthusiast', votes: 3 }
      ]);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!submittedQuestion.trim() || !user) return;

    const event = upcomingEvents.find(e => e.status === 'upcoming') || upcomingEvents[0];
    if (!event) return;

    try {
      await addDoc(collection(db, 'qaQuestions'), {
        eventId: event.id,
        text: submittedQuestion,
        userId: user.uid,
        userEmail: user.email,
        votes: 0,
        createdAt: Timestamp.now()
      });
      setSubmittedQuestion('');
      fetchQuestions(event.id);
    } catch (error) {
      console.error('Error submitting question:', error);
      // Still add to local state for UI feedback
      setQuestions([...questions, {
        id: Date.now().toString(),
        text: submittedQuestion,
        user: user.email,
        votes: 0
      }]);
      setSubmittedQuestion('');
    }
  };

  // Find live and upcoming events
  const liveEvent = upcomingEvents.find(e => e.status === 'live') || null;
  const upcomingEvent = upcomingEvents.find(e => e.status === 'upcoming') || upcomingEvents[0];
  const pastEventsList = pastEvents;

  if (loading) {
    return (
      <div className="space-y-16">
        <div className="text-center">
          <p className="text-muted-foreground">Loading Q&A events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {/* Featured Live Event */}
      {liveEvent && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="border-2 border-red-600 bg-red-50 overflow-hidden"
        >
          <div className="grid grid-cols-2">
            {/* Image */}
            <div className="relative aspect-[4/3]">
              <ImageWithFallback
                src={liveEvent.chefImage}
                alt={liveEvent.chef}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6 bg-red-600 text-white px-4 py-2 flex items-center gap-2 animate-pulse">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-xs uppercase tracking-widest">Live Now</span>
              </div>
            </div>

            {/* Content */}
            <div className="p-12 flex flex-col justify-center">
              <div className="w-1 h-12 bg-red-600 mb-6"></div>
              <h2 className="serif text-5xl mb-3">{liveEvent.title}</h2>
              <p className="text-sm mb-2">{liveEvent.chef}</p>
              <p className="text-xs text-muted-foreground mb-8">{liveEvent.chefBio}</p>

              <div className="flex items-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4" />
                  <span>{liveEvent.registered} watching</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{liveEvent.duration} minutes</span>
                </div>
              </div>

              <button className="w-full h-14 bg-red-600 text-white hover:bg-red-700 transition-colors text-sm uppercase tracking-widest flex items-center justify-center gap-3">
                <Video className="w-5 h-5" />
                <span>Join Live Session</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Upcoming Event */}
      {upcomingEvent && (
        <div>
          <div className="mb-8">
            <div className="w-1 h-12 bg-border mb-6"></div>
            <h2 className="serif text-6xl mb-4">Next Live Q&A</h2>
            <p className="text-muted-foreground">
              Join our monthly live sessions with renowned chefs and culinary experts
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border border-border overflow-hidden"
          >
            <div className="grid grid-cols-12">
              {/* Image */}
              <div className="col-span-5 relative aspect-[3/4]">
                <ImageWithFallback
                  src={upcomingEvent.chefImage}
                  alt={upcomingEvent.chef}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="col-span-7 p-12">
                <div className="mb-8">
                  <h3 className="serif text-5xl mb-4">{upcomingEvent.title}</h3>
                  <p className="text-sm mb-1">{upcomingEvent.chef}</p>
                  <p className="text-xs text-muted-foreground mb-6">{upcomingEvent.chefBio}</p>

                  <div className="flex items-center gap-8 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>{upcomingEvent.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span>{upcomingEvent.time}</span>
                    </div>
                  </div>
                </div>

                {/* Topics */}
                <div className="mb-8">
                  <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                    Topics to Cover
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {upcomingEvent.topics.map((topic) => (
                      <span
                        key={topic}
                        className="px-3 py-1.5 border border-border text-xs"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Submit Question */}
                {user && (
                  <div className="mb-8">
                    <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                      Submit Your Question
                    </h4>
                    <form onSubmit={handleSubmitQuestion} className="flex gap-3">
                      <input
                        type="text"
                        value={submittedQuestion}
                        onChange={(e) => setSubmittedQuestion(e.target.value)}
                        placeholder="What would you like to ask?"
                        className="flex-1 h-12 px-4 border border-border bg-background text-sm focus:outline-none focus:border-foreground transition-colors"
                      />
                      <button
                        type="submit"
                        className="px-6 h-12 border border-foreground hover:bg-foreground hover:text-background transition-all text-xs uppercase tracking-widest"
                      >
                        Submit
                      </button>
                    </form>
                    {questions.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        ✓ Question submitted! We'll review it before the session.
                      </p>
                    )}
                  </div>
                )}

                {/* Registration */}
                <div className="flex items-center gap-4">
                  <button className="flex-1 h-12 bg-foreground text-background hover:bg-foreground/90 transition-colors text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                    <Bell className="w-4 h-4" />
                    <span>Set Reminder</span>
                  </button>
                  <div className="text-sm text-muted-foreground">
                    {upcomingEvent.registered} registered
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Past Sessions */}
      {pastEventsList.length > 0 && (
        <div className="border-t border-border pt-16">
          <div className="mb-8">
            <h2 className="serif text-5xl mb-4">Past Sessions</h2>
            <p className="text-muted-foreground">
              Watch recordings of previous Q&A sessions
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {pastEventsList.map((event, index) => (
              <motion.article
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border border-border group hover:border-foreground transition-colors cursor-pointer"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <ImageWithFallback
                    src={event.chefImage}
                    alt={event.chef}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 border-2 border-white rounded-full flex items-center justify-center">
                      <Video className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-background px-3 py-1.5 text-xs">
                    {event.duration} min
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="serif text-2xl mb-2 group-hover:text-muted-foreground transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-sm mb-1">{event.chef}</p>
                  <p className="text-xs text-muted-foreground mb-4">{event.date}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>{event.registered} views</span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveQA;
